/**
 * Données de réputation centralisées (avis, notes).
 *
 * À UTILISER UNIQUEMENT POUR DES VALEURS RÉELLES ET VÉRIFIABLES.
 * Inventer des notes / avis fictifs déclenche un "Manual Action" Google
 * (perte d'éligibilité aux rich results et risque de désindexation).
 *
 * PLACEMENT : la note n'est rattachée à AUCUN nœud JSON-LD, voir
 * `RATING_IN_JSONLD` ci-dessous. Et si elle revenait un jour, elle n'irait
 * QU'AU nœud `LocalBusiness` / `Organization` (l'entité = l'entreprise, cf.
 * jsonLd.ts). NE JAMAIS la coller sur un `Service` (Google interdit les avis
 * sur ce type → GSC "Type d'objet non valide pour le champ <parent_node>"),
 * ni la cloner sur chaque `Product` (note d'entreprise répliquée =
 * "self-serving", non représentative du produit → risque). Pour de vraies
 * étoiles par véhicule/trajet, il faut de vrais avis PAR ITEM, pas la note
 * globale.
 *
 * Source de la valeur AFFICHÉE : la fiche Google My Business, qui est la
 * seule mesure vérifiable qu'on ait. Elle sert l'écran, pas le balisage.
 *
 * ⛔⛔ DEUX USAGES À NE PAS CONFONDRE, ET C'EST TOUT LE SUJET DE CE FICHIER.
 *
 *   AFFICHER la note à l'écran   → AUTORISÉ, et on le fait (3 composants).
 *   La DÉCLARER en JSON-LD       → INTERDIT ici, voir `RATING_IN_JSONLD`.
 *
 * `AGGREGATE_RATING` reste donc la vérité affichée, et il ne faut PAS le
 * passer à `null` pour retirer le balisage : 🟢 MESURE 2026-09-25, trois
 * composants le lisent pour l'écran (`ChauffeurForfaitsTrust`,
 * `RentalPricesTrust`, `ReviewsSection`), et `ReviewsSection` rend `null`
 * quand il est absent. Le mettre à `null` effacerait la preuve sociale du
 * site entier pour corriger un problème de balisage. C'est `RATING_IN_JSONLD`
 * qui commande le balisage, lui seul.
 *
 * La doc de GSC se trouve dans docs/seo/structured-data.md.
 */

/**
 * ⛔ Le nœud `aggregateRating` est-il injecté dans le JSON-LD ? NON.
 *
 * 🟢 VÉRIFIÉ 2026-09-25 dans la documentation officielle Google
 * (developers.google.com/search/docs/appearance/structured-data/review-snippet) :
 * `LocalBusiness` et `Organization` ne sont acceptés que pour des avis de
 * TIERS, et « si l'entité évaluée contrôle les avis qui la concernent, ses
 * pages sont inéligibles à l'affichage en étoiles ». Google nomme
 * explicitement le cas des widgets d'avis Google republiés par l'entreprise
 * sur son propre site : c'est exactement ce que faisait ce fichier.
 *
 * Donc ce balisage ne rapportait **rien** (inéligible par règle, pas par
 * hasard) et exposait un site client en production au motif « avis
 * auto-décernés ». Le fichier `app/data/googleReviews.ts` avait déjà écarté
 * les nœuds `Review` par ce même raisonnement : la note globale échappait à
 * la règle qu'il énonçait lui-même.
 *
 * ⚠️ Ce drapeau n'a pas de version « conforme » sur ce site. Passer par
 * Trustpilot n'y changerait rien : Google vise aussi les widgets tiers
 * intégrés. Il ne se rallume que si Google change sa règle, avec la date de
 * relecture en commentaire.
 */
export const RATING_IN_JSONLD = false;

export type AggregateRating = {
  /** Note moyenne (1 à 5). */
  ratingValue: number;
  /** Nombre total d'avis. */
  reviewCount: number;
  /** Plafond de la notation (par défaut 5). */
  bestRating?: number;
  /** Plancher de la notation (par défaut 1). */
  worstRating?: number;
  /** Source publique vérifiable (pour traçabilité, pas exposée). */
  source: string;
};

/**
 * Données RÉELLES Google My Business, synchronisées le 2026-06-29 depuis la
 * fiche (place_id ChIJZyLnm7zUmikR6qmaUYKKEno) : 38 avis, tous 5★ → moyenne
 * 5,0/5. Source idéale (Google s'en sert pour les rich results). Les avis
 * verbatim sont dans app/data/googleReviews.ts.
 *
 * Mettre à `null` pour ne PAS inclure dans le JSON-LD (évite le warning
 * "facultatif" mais évite aussi de pousser des données incertaines).
 */
export const AGGREGATE_RATING: AggregateRating | null = {
  ratingValue: 5,
  reviewCount: 38,
  bestRating: 5,
  worstRating: 1,
  source: "Google My Business (fiche KS Rent Services, 38 avis)",
};

/**
 * Construit l'objet schema.org `AggregateRating`, ou `undefined`.
 *
 * ⛔ Rend `undefined` tant que `RATING_IN_JSONLD` est `false`, donc toujours
 * aujourd'hui. Les appelants (`jsonLd.ts`) gèrent déjà ce cas : le champ est
 * simplement absent du nœud `LocalBusiness`.
 */
export function buildAggregateRatingSchema():
  | Record<string, unknown>
  | undefined {
  if (!RATING_IN_JSONLD || !AGGREGATE_RATING) return undefined;
  return {
    "@type": "AggregateRating",
    ratingValue: AGGREGATE_RATING.ratingValue,
    reviewCount: AGGREGATE_RATING.reviewCount,
    bestRating: AGGREGATE_RATING.bestRating ?? 5,
    worstRating: AGGREGATE_RATING.worstRating ?? 1,
  };
}
